import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import XavvyFormRenderer from '../../XavvyFormRenderer';
import { registerWidget } from '../widgetRegistry';
import { useLayoutCtx } from '../LayoutContext';
import { authHeaders } from '../../CaseClient';

// Granular Case-surface widget — read-only Overview content: description,
// timestamps, and either the case-type's detail xavvy form (when one is
// configured) or a key/value list of schema + custom fields.
//
// Editing is intentionally NOT in this widget for v1: the inline edit-mode
// in CaseDetail keeps Overview + edit shared state, and replicating that
// across granular widgets is the L4 events story. For now, customers
// composing a custom layout from granular widgets get a read-only
// Overview; admins who want the full edit flow keep `case.builtin`.

function CaseSummaryFields({ config }) {
  const { token, case: caseRow } = useLayoutCtx();
  const [detailFormDef, setDetailFormDef] = useState(null);

  useEffect(() => {
    if (!caseRow) { setDetailFormDef(null); return; }
    const cfg = caseRow.case_type?.form_config_json;
    const parsedCfg = typeof cfg === 'string'
      ? (() => { try { return JSON.parse(cfg); } catch { return null; } })()
      : cfg;
    const detailId = parsedCfg?.detail_form_id;
    if (!detailId) { setDetailFormDef(null); return; }
    let cancelled = false;
    axios.get(`${API_BASE_URL}/xavvy-forms/${detailId}`, { headers: authHeaders(token) })
      .then(r => {
        if (cancelled) return;
        const def = r.data?.definition;
        const parsed = typeof def === 'string'
          ? (() => { try { return JSON.parse(def); } catch { return null; } })()
          : def;
        setDetailFormDef(parsed || null);
      })
      .catch(() => { if (!cancelled) setDetailFormDef(null); });
    return () => { cancelled = true; };
  }, [caseRow?.case_type?.form_config_json, caseRow?.id, token]);

  if (!caseRow) {
    return (
      <div style={{ padding: '14px 20px', color: 'var(--text-secondary)', fontSize: '13px' }}>
        No case loaded.
      </div>
    );
  }

  const showDescription = config.show_description !== false;
  const showTimestamps = config.show_timestamps !== false;
  const schemaFields = caseRow.case_type?.schema_json?.fields || [];

  const Field = ({ label, children }) => (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '13px', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{children}</div>
    </div>
  );

  return (
    <div style={{ padding: '20px' }}>
      {showDescription && (
        <Field label="Description">
          {caseRow.description || (
            <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>No description.</span>
          )}
        </Field>
      )}
      {showTimestamps && caseRow.created_at && (
        <Field label="Created">{new Date(caseRow.created_at).toLocaleString()}</Field>
      )}
      {showTimestamps && caseRow.sla_resolution_deadline && (
        <Field label="Resolution due">{new Date(caseRow.sla_resolution_deadline).toLocaleString()}</Field>
      )}
      {detailFormDef ? (
        <Field label={`${caseRow.case_type?.icon || '📋'} ${caseRow.case_type?.name || 'Case'} fields`}>
          <XavvyFormRenderer
            token={token}
            definition={detailFormDef}
            initialValues={caseRow.custom_fields_json || {}}
            readOnly={true}
          />
        </Field>
      ) : (schemaFields.length > 0 || (caseRow.custom_fields_json && Object.keys(caseRow.custom_fields_json).length > 0)) && renderCustomFields(caseRow, schemaFields)}
    </div>
  );
}

function renderCustomFields(caseRow, schemaFields) {
  const cf = caseRow.custom_fields_json || {};
  const cfLower = {};
  Object.entries(cf).forEach(([k, v]) => { cfLower[k.toLowerCase()] = { k, v }; });
  const seen = new Set();
  const rows = schemaFields.map(f => {
    const hit = cfLower[(f.key || '').toLowerCase()];
    if (hit) seen.add(hit.k);
    const v = hit ? hit.v : undefined;
    return { key: f.key, label: f.label || f.key, value: v, type: f.type };
  });
  Object.entries(cf).forEach(([k, v]) => {
    if (k.startsWith('_') || seen.has(k)) return;
    rows.push({ key: k, label: k, value: v });
  });
  const fmt = (row) => {
    const v = row.value;
    if (v === undefined || v === null || v === '') {
      return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
    }
    if (row.type === 'checkbox') return v ? 'Yes' : 'No';
    if (Array.isArray(v)) {
      return v.length === 0
        ? <span style={{ color: 'var(--text-tertiary)' }}>—</span>
        : v.join(', ');
    }
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  };
  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', marginBottom: '4px' }}>Custom fields</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {rows.map(row => (
          <div key={row.key} style={{ display: 'flex', gap: '10px', fontSize: '13px' }}>
            <span style={{ color: 'var(--text-secondary)', minWidth: '140px' }}>{row.label}</span>
            <span>{fmt(row)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

registerWidget({
  type: 'case.summary_fields',
  categories: ['case'],
  palette_label: 'Case summary fields',
  description: 'Read-only description, timestamps, and case-type fields (xavvy detail form when configured, else key/value list).',
  Component: CaseSummaryFields,
  configSchema: {
    fields: [
      { type: 'boolean', name: 'show_description', label: 'Show description', default: true },
      { type: 'boolean', name: 'show_timestamps', label: 'Show created / SLA timestamps', default: true },
    ],
  },
  publishes: [],
  subscribes: ['xv.CaseSaved', 'xv.RefreshContext'],
  group: 'Case',
});
